import { useEffect, useState } from 'react';
import agent from '../../app/api/agent';
import { FitmentFieldValues, getVisibleFitmentFields, normalizeFitmentKey } from '../../app/utils/fitmentFields';

type Field = ReturnType<typeof getVisibleFitmentFields>[number];
type Option = { label: string; value: string };
type PlannedField = { key: string; cacheKey: string; parents: FitmentFieldValues };
type FieldState = { cacheKey: string; options: Option[]; loading: boolean };

// Owned by one product page and shared by its Add and Edit selectors. Never
// reuse merchant options across shops/catalogs, or after a successful save.
export class FitmentOptionsCache {
    private entries = new Map<string, { options: Option[]; expires: number }>();

    get(key: string) {
        const entry = this.entries.get(key);
        if (entry && entry.expires > Date.now()) return entry.options;
        this.entries.delete(key);
        return undefined;
    }

    set(key: string, options: Option[]) {
        this.entries.delete(key);
        this.entries.set(key, { options, expires: Date.now() + 120_000 });
        if (this.entries.size > 128) this.entries.delete(this.entries.keys().next().value!);
    }
}

function planOptions(fields: Field[], values: FitmentFieldValues, requestedFields: Field[]): PlannedField[] {
    const requested = new Set(requestedFields.map(normalizeFitmentKey));
    const schema = fields.map((field) => [normalizeFitmentKey(field), !!field.isFilterable]);
    const parents: FitmentFieldValues = {};
    const plan: PlannedField[] = [];
    for (const field of fields) {
        const key = normalizeFitmentKey(field);
        if (requested.has(key)) plan.push({ key, cacheKey: JSON.stringify([schema, key, parents]), parents: { ...parents } });
        const value = values[key]?.trim();
        if (value) parents[key] = value;
        else if (field.isFilterable) break;
    }
    return plan;
}

export function useFitmentOptions(
    cache: FitmentOptionsCache,
    fields: Field[],
    values: FitmentFieldValues | undefined,
    enabled: boolean,
    delayMs = 0,
    requestedFields = fields,
) {
    const plan = enabled && values ? planOptions(fields, values, requestedFields) : [];
    // Depend on each dropdown's upstream selections, not its own selected text.
    // Typing filters that dropdown locally; only downstream options need the API.
    const planKey = JSON.stringify(plan);
    const orderKey = JSON.stringify(fields.map(normalizeFitmentKey));
    const [state, setState] = useState<{ cache: FitmentOptionsCache; fields: Record<string, FieldState> }>({ cache, fields: {} });

    useEffect(() => {
        const planned: PlannedField[] = JSON.parse(planKey);
        const fieldOrder: string[] = JSON.parse(orderKey);
        const next: Record<string, FieldState> = {};
        const missing = planned.filter((field) => {
            const options = cache.get(field.cacheKey);
            next[field.key] = { cacheKey: field.cacheKey, options: options ?? [], loading: options === undefined };
            return options === undefined;
        });
        setState({ cache, fields: next });
        if (!missing.length) return;

        const controller = new AbortController();
        const load = async () => {
            const loaded = { ...next };
            try {
                const fieldValues = Object.assign({}, ...missing.map((field) => field.parents));
                const result = await agent.Vehicles.filterOptions('', '', '', fieldValues, fieldOrder,
                    missing.map((field) => field.key), controller.signal);
                if (controller.signal.aborted) return;
                for (const field of missing) {
                    const options = (result.optionsByField?.[field.key] ?? []).map((value) => ({ label: value, value }));
                    cache.set(field.cacheKey, options);
                    loaded[field.key] = { cacheKey: field.cacheKey, options, loading: false };
                }
            } catch {
                // An unsuccessful request must not poison the cache. Custom
                // values remain editable, and a later selection can retry.
                for (const field of missing) loaded[field.key] = { ...next[field.key], loading: false };
            } finally {
                if (!controller.signal.aborted) setState({ cache, fields: loaded });
            }
        };
        // Picking a saved option loads the next field immediately. Only typing
        // waits briefly, avoiding a request for every partial make/model value.
        const timer = delayMs ? window.setTimeout(load, delayMs) : undefined;
        if (!delayMs) void load();
        return () => {
            window.clearTimeout(timer);
            controller.abort();
        };
    }, [cache, planKey, orderKey, delayMs]);

    const options: Record<string, Option[]> = {};
    const loading: Record<string, boolean> = {};
    for (const field of plan) {
        const current = state.cache === cache && state.fields[field.key]?.cacheKey === field.cacheKey
            ? state.fields[field.key] : undefined;
        const cached = current ? undefined : cache.get(field.cacheKey);
        options[field.key] = current?.options ?? cached ?? [];
        loading[field.key] = current?.loading ?? (cached === undefined);
    }
    return { options, loading };
}
